import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Settings as SettingsIcon, Loader2, Save, Radio, Bluetooth, Bell,
  Upload, Database, ExternalLink, Map as MapIcon, Sparkles, ShieldAlert,
  Mail, MessageSquare, Send, Trash2, AlertTriangle, Search, Check,
  CheckCircle2, XCircle, Zap,
} from "lucide-react";

/* ── TABS DEFINITION ──────────────────────────────────────────────────
   Each tab maps to a section with its own save action.
   ──────────────────────────────────────────────────────────────────── */
const TABS = [
  { id: "general",      label: "Général",            icon: SettingsIcon, group: "Configuration" },
  { id: "detection",    label: "Détection arrêts",   icon: Bluetooth,    group: "Configuration" },
  { id: "notify",       label: "Notifications",      icon: Bell,         group: "Configuration" },
  { id: "navixy",       label: "Logitrak · GPS",       icon: Radio,        group: "Intégrations" },
  { id: "corridor",     label: "Corridor sécurité",  icon: ShieldAlert,  group: "Intégrations" },
  { id: "osrm",         label: "Tracés OSRM",        icon: MapIcon,      group: "Données" },
  { id: "gtfs",         label: "Import GTFS",        icon: Database,     group: "Données" },
  { id: "mirror",       label: "Tournées miroir",    icon: Sparkles,     group: "Données" },
  { id: "danger",       label: "Zone dangereuse",    icon: AlertTriangle,group: "Avancé", danger: true },
];

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  const [logitrakHash, setLogitrakHash] = useState("");
  const [saving, setSaving] = useState(false);
  const [notifSettings, setNotifSettings] = useState({ email_enabled: true, sms_enabled: false });
  const [notifChannels, setNotifChannels] = useState({ email: { configured: false }, sms: { configured: false } });
  const [activeTab, setActiveTab] = useState("general");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/logibus/admin/settings");
      setSettings(data);
      setThresholds(data.thresholds || {});
      setLogitrakHash(data.navixy_hash || "");
      const ns = await api.get("/logibus/admin/notifications-settings");
      setNotifSettings(ns.data.settings || { email_enabled: true, sms_enabled: false });
      setNotifChannels(ns.data.channels || {});
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateField = (k, v) => setThresholds((t) => ({ ...t, [k]: v }));

  const saveThresholds = async () => {
    setSaving(true);
    try {
      await api.post("/logibus/admin/settings/delay-threshold", thresholds);
      toast.success("Paramètres enregistrés");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const saveLogitrak = async (hashOverride) => {
    setSaving(true);
    const hash = typeof hashOverride === "string" ? hashOverride : logitrakHash;
    try {
      await api.post("/logibus/admin/settings/navixy", { navixy_hash: hash });
      toast.success(hash ? "Clé Logitrak enregistrée" : "Clé Logitrak effacée");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const filteredTabs = useMemo(() => {
    if (!search.trim()) return TABS;
    const q = search.toLowerCase();
    return TABS.filter((t) => t.label.toLowerCase().includes(q) || t.id.includes(q));
  }, [search]);

  // When the search narrows, auto-jump to first match
  useEffect(() => {
    if (search.trim() && filteredTabs.length > 0 && !filteredTabs.find((t) => t.id === activeTab)) {
      setActiveTab(filteredTabs[0].id);
    }
  }, [search, filteredTabs, activeTab]);

  if (!settings || !thresholds) {
    return <div className="p-10 text-zinc-500 font-mono text-sm">Chargement…</div>;
  }

  const groups = ["Configuration", "Intégrations", "Données", "Avancé"];

  return (
    <div className="flex flex-col lg:flex-row min-h-[calc(100vh-64px)]" data-testid="admin-settings-page">
      {/* ── Sidebar ─────────────────────────────────────────────── */}
      <aside className="lg:w-72 lg:min-h-full bg-white border-b lg:border-b-0 lg:border-r border-zinc-200 px-4 py-6 lg:py-8 shrink-0">
        <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Configuration</div>
        <h1 className="font-display font-black uppercase text-2xl tracking-tighter mt-1 mb-4">Paramètres</h1>

        <div className="relative mb-5">
          <Search size={12} className="absolute left-2 top-2 text-zinc-400 pointer-events-none" />
          <input
            data-testid="settings-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un réglage…"
            className="w-full border border-zinc-300 pl-7 pr-2 py-1.5 text-xs font-mono focus:outline-none focus:border-zinc-900"
          />
        </div>

        <nav className="space-y-5" data-testid="settings-tabs">
          {groups.map((grp) => {
            const items = filteredTabs.filter((t) => t.group === grp);
            if (!items.length) return null;
            return (
              <div key={grp}>
                <div className="text-[9px] font-mono uppercase tracking-[0.3em] text-zinc-400 mb-1.5">{grp}</div>
                <ul className="space-y-0.5">
                  {items.map((t) => {
                    const Icon = t.icon;
                    const active = activeTab === t.id;
                    return (
                      <li key={t.id}>
                        <button
                          onClick={() => setActiveTab(t.id)}
                          data-testid={`settings-tab-${t.id}`}
                          className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium transition-colors ${
                            active
                              ? t.danger
                                ? "bg-red-100 text-red-800 border-l-4 border-red-600"
                                : "bg-zinc-900 text-[#FFC60A] border-l-4 border-[#FFC60A]"
                              : t.danger
                                ? "text-red-700 hover:bg-red-50"
                                : "text-zinc-700 hover:bg-zinc-100"
                          }`}>
                          <Icon size={13} />
                          <span className="flex-1">{t.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* Status summary */}
        <div className="mt-8 pt-5 border-t border-zinc-200">
          <div className="text-[9px] font-mono uppercase tracking-[0.3em] text-zinc-400 mb-2">Statut</div>
          <div className="space-y-1.5 text-[11px]">
            <StatusLine label="Logitrak"   ok={!!settings.navixy_hash} okLabel="Connecté" koLabel="Non configuré" />
            <StatusLine label="Email"    ok={!!notifChannels.email?.configured} okLabel="Configuré" koLabel="Non configuré" />
            <StatusLine label="SMS"      ok={!!notifChannels.sms?.configured} okLabel="Configuré" koLabel="Non configuré" />
          </div>
        </div>
      </aside>

      {/* ── Content ─────────────────────────────────────────────── */}
      <main className="flex-1 px-6 py-6 lg:px-10 lg:py-10 max-w-4xl">
        {activeTab === "general" && (
          <GeneralTab thresholds={thresholds} updateField={updateField} save={saveThresholds} saving={saving} />
        )}
        {activeTab === "detection" && (
          <DetectionTab thresholds={thresholds} updateField={updateField} save={saveThresholds} saving={saving} />
        )}
        {activeTab === "notify" && (
          <NotifyTab notifSettings={notifSettings} setNotifSettings={setNotifSettings} notifChannels={notifChannels} reload={load} />
        )}
        {activeTab === "navixy" && (
          <LogitrakTab logitrakHash={logitrakHash} setLogitrakHash={setLogitrakHash} saveLogitrak={saveLogitrak} saving={saving} settings={settings} />
        )}
        {activeTab === "corridor" && (
          <CorridorTab thresholds={thresholds} updateField={updateField} save={saveThresholds} saving={saving} hasLogitrak={!!settings.navixy_hash} />
        )}
        {activeTab === "osrm" && (
          <OsrmTab />
        )}
        {activeTab === "gtfs" && (
          <GtfsTab />
        )}
        {activeTab === "mirror" && (
          <MirrorTab />
        )}
        {activeTab === "danger" && (
          <ResetSchoolDataSection />
        )}
      </main>
    </div>
  );
}

/* ── Shared helpers ─────────────────────────────────────────────────── */
function StatusLine({ label, ok, okLabel, koLabel }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-zinc-500 font-mono uppercase tracking-widest text-[9px]">{label}</span>
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${ok ? "text-emerald-700" : "text-zinc-400"}`}>
        {ok ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
        {ok ? okLabel : koLabel}
      </span>
    </div>
  );
}

function TabHeader({ title, subtitle, icon: Icon }) {
  return (
    <header className="mb-6">
      <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 inline-flex items-center gap-1.5">
        <Icon size={12} /> Réglage
      </div>
      <h2 className="font-display font-black uppercase text-3xl tracking-tighter mt-1">{title}</h2>
      {subtitle && <p className="text-sm text-zinc-600 mt-1">{subtitle}</p>}
    </header>
  );
}

function NumField({ label, value, onChange, testId, unit }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}{unit && <span className="ml-1 text-zinc-400">({unit})</span>}</span>
      <input type="number" data-testid={testId} value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1 w-full border border-zinc-900 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#FFC60A]" />
    </label>
  );
}

function SaveBtn({ onClick, disabled, testId, label = "Enregistrer" }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testId}
      className="bg-[#FFC60A] border border-zinc-900 px-4 py-2 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2 hover:bg-[#ffd43a] disabled:opacity-50 shadow-[3px_3px_0_0_#0A0A0B]">
      {disabled ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {label}
    </button>
  );
}

/* ── Tab: Général (seuils retard) ───────────────────────────────────── */
function GeneralTab({ thresholds, updateField, save, saving }) {
  return (
    <>
      <TabHeader title="Seuils de retard" icon={SettingsIcon}
        subtitle="Définissez les niveaux de retard à partir desquels une mission est marquée comme légère, importante ou critique." />
      <div className="grid grid-cols-3 gap-3">
        <NumField label="Léger" unit="s"     testId="thr-light"    value={thresholds.light}    onChange={(v) => updateField("light", v)} />
        <NumField label="Important" unit="s" testId="thr-medium"   value={thresholds.medium}   onChange={(v) => updateField("medium", v)} />
        <NumField label="Critique" unit="s"  testId="thr-critical" value={thresholds.critical} onChange={(v) => updateField("critical", v)} />
      </div>
      <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
        Exemple : <strong>Léger 60s · Important 180s · Critique 300s</strong> signifie qu&apos;un bus avec 1 minute de retard sera marqué « léger »,
        au-delà de 3 minutes « important », et au-delà de 5 minutes « critique » (envoi d&apos;alerte automatique).
      </p>
      <div className="mt-6"><SaveBtn onClick={save} disabled={saving} testId="save-thresholds" label="Enregistrer les seuils" /></div>
    </>
  );
}

/* ── Tab: Détection arrêts ──────────────────────────────────────────── */
function DetectionTab({ thresholds, updateField, save, saving }) {
  return (
    <>
      <TabHeader title="Détection passage arrêt" icon={Bluetooth}
        subtitle="Paramètres de détection automatique du passage du bus aux arrêts (rayon GPS, vitesse max, anti-rebond)." />
      <div className="grid grid-cols-3 gap-3">
        <NumField label="Rayon" unit="m"          testId="thr-radius"  value={thresholds.detection_radius_m}     onChange={(v) => updateField("detection_radius_m", v)} />
        <NumField label="Vitesse max" unit="km/h" testId="thr-speed"   value={thresholds.detection_max_speed_kmh} onChange={(v) => updateField("detection_max_speed_kmh", v)} />
        <NumField label="Anti-doublon" unit="min" testId="thr-dedup"   value={thresholds.dedup_window_minutes}   onChange={(v) => updateField("dedup_window_minutes", v)} />
      </div>
      <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
        Le bus est considéré « à l&apos;arrêt » s&apos;il entre dans le rayon avec une vitesse inférieure à la limite. L&apos;anti-doublon empêche
        le double-comptage d&apos;un même arrêt pendant cette durée.
      </p>
      <div className="mt-6"><SaveBtn onClick={save} disabled={saving} testId="save-detection" /></div>

      <hr className="my-8 border-zinc-200" />

      <TabHeader title="Notification client" icon={Bell}
        subtitle="Seuils de retard à partir desquels les parents sont notifiés, par type de tournée." />
      <div className="grid grid-cols-3 gap-3">
        <NumField label="Normal" unit="s"   testId="thr-notif-normal" value={thresholds.notify_normal_seconds}   onChange={(v) => updateField("notify_normal_seconds", v)} />
        <NumField label="Scolaire" unit="s" testId="thr-notif-scol"   value={thresholds.notify_scolaire_seconds} onChange={(v) => updateField("notify_scolaire_seconds", v)} />
        <NumField label="PMR" unit="s"      testId="thr-notif-pmr"    value={thresholds.notify_pmr_seconds}      onChange={(v) => updateField("notify_pmr_seconds", v)} />
      </div>
      <label className="flex items-center gap-2 mt-3 text-xs">
        <input type="checkbox" data-testid="thr-notif-crit" checked={!!thresholds.notify_critical_always}
          onChange={(e) => updateField("notify_critical_always", e.target.checked)} />
        Toujours notifier sur retard <strong>CRITICAL</strong> (ignore les seuils ci-dessus)
      </label>
      <div className="mt-6"><SaveBtn onClick={save} disabled={saving} testId="save-thresholds-notif" /></div>
    </>
  );
}

/* ── Tab: Logitrak ────────────────────────────────────────────────────── */
function LogitrakTab({ logitrakHash, setLogitrakHash, saveLogitrak, saving, settings }) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      // Use the existing sync endpoint as a connectivity probe (it reads trackers list).
      const { data } = await api.post("/navixy-sync/run", {});
      setTestResult({ ok: true, data });
      toast.success("Connexion Logitrak OK · " + (data?.synced_trackers ?? data?.trackers_count ?? "?") + " trackers");
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e.message;
      setTestResult({ ok: false, error: msg });
      toast.error("Connexion Logitrak échouée : " + msg);
    } finally { setTesting(false); }
  };

  const isConnected = !!settings.navixy_hash;

  return (
    <>
      <TabHeader title="Clé API Logitrak" icon={Radio}
        subtitle="Hash de session Logitrak pour le tracking GPS temps réel. Sans clé, l'app utilise le simulateur GTFS." />

      <div className={`border-l-4 px-4 py-3 mb-5 text-sm ${isConnected ? "border-emerald-500 bg-emerald-50" : "border-amber-500 bg-amber-50"}`}>
        <div className="flex items-center gap-2 font-bold">
          {isConnected ? <CheckCircle2 size={14} className="text-emerald-700" /> : <XCircle size={14} className="text-amber-700" />}
          <span className={isConnected ? "text-emerald-800" : "text-amber-800"}>
            {isConnected ? "Clé configurée · Tracking réel actif" : "Aucune clé · Mode simulateur GTFS"}
          </span>
        </div>
      </div>

      <label className="block">
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Hash de session Logitrak</span>
        <input data-testid="navixy-hash" value={logitrakHash} onChange={(e) => setLogitrakHash(e.target.value)}
          placeholder="hash session Logitrak (ex: a1b2c3d4e5f6...)"
          className="mt-1 w-full border border-zinc-900 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#FFC60A]" />
      </label>

      <div className="mt-3 flex flex-wrap gap-2">
        <SaveBtn onClick={() => saveLogitrak()} disabled={saving} testId="save-navixy" />
        <button data-testid="clear-navixy" onClick={() => { setLogitrakHash(""); saveLogitrak(""); }}
          className="border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-100">
          Effacer
        </button>
        <button data-testid="test-navixy" onClick={testConnection} disabled={testing || !isConnected}
          className="bg-zinc-900 text-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1.5">
          {testing ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />} Tester la connexion
        </button>
      </div>

      {testResult && (
        <div data-testid="navixy-test-result" className={`mt-4 border px-3 py-2 text-xs font-mono ${testResult.ok ? "border-emerald-600 bg-emerald-50 text-emerald-900" : "border-red-600 bg-red-50 text-red-900"}`}>
          {testResult.ok
            ? <>✅ Connexion OK · Trackers synchronisés : <strong>{testResult.data?.synced_trackers ?? testResult.data?.trackers_count ?? "?"}</strong></>
            : <>❌ Échec : {testResult.error}</>}
        </div>
      )}

      <details className="mt-6 text-xs text-zinc-600">
        <summary className="cursor-pointer font-bold text-zinc-800 hover:text-zinc-900">📘 Comment obtenir le hash ?</summary>
        <ol className="mt-2 ml-4 list-decimal space-y-1 leading-relaxed">
          <li>Connectez-vous à <a className="underline text-zinc-900" target="_blank" rel="noreferrer" href="https://panel.navixy.com">votre panneau Logitrak</a></li>
          <li>Cliquez sur votre nom en haut à droite → <strong>Compte</strong></li>
          <li>Onglet <strong>API</strong> → copier le <strong>Hash de session</strong> (format : <code className="bg-zinc-100 px-1">a1b2c3...</code>)</li>
          <li>Coller ci-dessus et cliquer <strong>Enregistrer</strong></li>
          <li>Cliquer <strong>Tester la connexion</strong> pour vérifier</li>
        </ol>
        <p className="mt-2 text-zinc-500">
          ⚠️ Le hash expire si vous restez déconnecté du panel pendant plusieurs semaines.
          Si le test échoue avec une erreur 400, régénérez un nouveau hash.
        </p>
      </details>
    </>
  );
}

/* ── Tab: Notifications ─────────────────────────────────────────────── */
function NotifyTab({ notifSettings, setNotifSettings, notifChannels, reload }) {
  const [testTo, setTestTo] = useState("");
  const [testChannel, setTestChannel] = useState("email");
  const [testSending, setTestSending] = useState(false);

  const save = async () => {
    try {
      await api.post("/logibus/admin/notifications-settings", notifSettings);
      toast.success("Préférences notifications enregistrées");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <>
      <TabHeader title="Notifications Email / SMS" icon={Mail}
        subtitle="Sur chaque retard CRITICAL, LOGIBUS envoie automatiquement un email (Resend) et/ou un SMS (Twilio) aux parents." />

      <div className="flex flex-wrap gap-2 mb-4 text-[11px] font-mono">
        <span className={`px-2 py-1 border ${notifChannels.email?.configured ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-zinc-50 text-zinc-500 border-zinc-200"}`}>
          <Mail size={11} className="inline mr-1" /> Email · {notifChannels.email?.configured ? "configuré" : "non configuré"}
        </span>
        <span className={`px-2 py-1 border ${notifChannels.sms?.configured ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-zinc-50 text-zinc-500 border-zinc-200"}`}>
          <MessageSquare size={11} className="inline mr-1" /> SMS · {notifChannels.sms?.configured ? "configuré" : "non configuré"}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" data-testid="notif-email-toggle" checked={!!notifSettings.email_enabled}
            onChange={(e) => setNotifSettings((s) => ({ ...s, email_enabled: e.target.checked }))} />
          Email parents sur retard CRITICAL
        </label>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" data-testid="notif-sms-toggle" checked={!!notifSettings.sms_enabled}
            onChange={(e) => setNotifSettings((s) => ({ ...s, sms_enabled: e.target.checked }))} />
          SMS parents sur retard CRITICAL <span className="text-[10px] text-zinc-500">(opt-in)</span>
        </label>
      </div>

      <div className="mt-4">
        <SaveBtn onClick={save} disabled={false} testId="save-notif-btn" />
      </div>

      <hr className="my-8 border-zinc-200" />

      <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 mb-2 inline-flex items-center gap-1.5">
        <Send size={11} /> Test d&apos;envoi
      </div>
      <div className="flex flex-wrap gap-2">
        <select data-testid="test-channel-select" value={testChannel} onChange={(e) => setTestChannel(e.target.value)}
          className="border border-zinc-900 px-2 py-2 font-mono text-xs">
          <option value="email">Email</option>
          <option value="sms">SMS</option>
        </select>
        <input data-testid="test-to-input" value={testTo} onChange={(e) => setTestTo(e.target.value)}
          placeholder={testChannel === "email" ? "destinataire@example.com" : "+41790000000"}
          className="flex-1 min-w-[200px] border border-zinc-900 px-3 py-2 font-mono text-xs" />
        <button data-testid="test-send-btn" disabled={testSending || !testTo}
          onClick={async () => {
            setTestSending(true);
            try {
              const { data } = await api.post("/logibus/admin/test-notification", {
                channel: testChannel, to: testTo,
                subject: "[LOGIBUS] Test", body: "Notification de test depuis LOGIBUS.",
              });
              if (data.status === "sent") toast.success(`Envoyé (id=${data.id || data.sid || "?"})`);
              else if (data.status === "skipped") toast.warning(`Ignoré : ${data.reason}`);
              else toast.error(`Échec : ${data.error || "inconnu"}`);
            } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
            finally { setTestSending(false); }
          }}
          className="bg-zinc-900 text-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1">
          {testSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Tester
        </button>
      </div>
      <p className="text-[10px] font-mono text-zinc-500 mt-2">
        Pour activer les vrais envois : ajoutez <code className="bg-zinc-100 px-1">RESEND_API_KEY</code>,
        <code className="bg-zinc-100 px-1 ml-1">TWILIO_ACCOUNT_SID</code>, <code className="bg-zinc-100 px-1">TWILIO_AUTH_TOKEN</code> et
        <code className="bg-zinc-100 px-1 ml-1">TWILIO_FROM_NUMBER</code> dans <code className="bg-zinc-100 px-1">/app/backend/.env</code>.
      </p>
    </>
  );
}

/* ── Tab: Corridor sécurité ─────────────────────────────────────────── */
function CorridorTab({ thresholds, updateField, save, saving, hasLogitrak }) {
  const [detectRunning, setDetectRunning] = useState(false);
  const [detectResult, setDetectResult] = useState(null);
  const [pushRunning, setPushRunning] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  return (
    <>
      <TabHeader title="Corridor de sécurité" icon={ShieldAlert}
        subtitle={`Surveille automatiquement chaque bus actif et déclenche un incident "Hors-itinéraire" si la position GPS sort d'un couloir de ${thresholds.geofence_buffer_m || 50} m autour du tracé OSRM de la ligne.`} />

      <div className="grid grid-cols-2 gap-3">
        <NumField label="Largeur corridor" unit="m"  testId="thr-geofence-buffer" value={thresholds.geofence_buffer_m}    onChange={(v) => updateField("geofence_buffer_m", v)} />
        <NumField label="Anti-doublon" unit="min"    testId="thr-geofence-dedup"  value={thresholds.geofence_dedup_minutes} onChange={(v) => updateField("geofence_dedup_minutes", v)} />
      </div>
      <div className="mt-4"><SaveBtn onClick={save} disabled={saving} testId="save-corridor" /></div>

      <hr className="my-6 border-zinc-200" />

      <div className="flex flex-wrap gap-2">
        <button data-testid="detect-deviations-btn" disabled={detectRunning}
          onClick={async () => {
            setDetectRunning(true); setDetectResult(null);
            try {
              const { data } = await api.post("/logibus/admin/detect-deviations-now");
              setDetectResult(data);
              toast.success(`${data.off_route} bus hors-itinéraire · ${data.incidents_created} incident(s) créé(s)`);
            } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
            finally { setDetectRunning(false); }
          }}
          className="border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-100 disabled:opacity-50 inline-flex items-center gap-1">
          {detectRunning ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />} Évaluer maintenant
        </button>
        <button data-testid="push-corridors-btn" disabled={pushRunning || !hasLogitrak}
          onClick={async () => {
            if (!confirm("Pousser tous les couloirs vers Logitrak en tant que zones ? (peut consommer votre quota zones)")) return;
            setPushRunning(true); setPushResult(null);
            try {
              const { data } = await api.post("/logibus/admin/push-corridors-to-navixy", {
                buffer_m: thresholds.geofence_buffer_m || 50,
                dedup_minutes: thresholds.geofence_dedup_minutes || 5,
              });
              setPushResult(data);
              toast.success(`${data.pushed}/${data.total_trips} zones poussées vers Logitrak`);
            } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
            finally { setPushRunning(false); }
          }}
          className="bg-zinc-900 text-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1">
          {pushRunning ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Pousser zones vers Logitrak
        </button>
      </div>
      {!hasLogitrak && <p className="text-[11px] text-amber-700 mt-2">⚠️ Nécessite la clé Logitrak (onglet « Logitrak · GPS »)</p>}

      {detectResult && (
        <div className="mt-3 border border-zinc-300 bg-zinc-50 p-3 text-xs font-mono" data-testid="detect-deviations-result">
          ✓ {detectResult.checked} bus évalués · {detectResult.skipped_slow ?? 0} ignorés (arrêt) ·{" "}
          <strong>{detectResult.off_route} hors-itinéraire</strong> · {detectResult.incidents_created} incident(s) créé(s)
        </div>
      )}
      {pushResult && (
        <div className="mt-3 border border-emerald-700 bg-emerald-50 p-3 text-xs font-mono" data-testid="push-zones-result">
          ✅ {pushResult.pushed}/{pushResult.total_trips} couloirs poussés vers Logitrak
        </div>
      )}
    </>
  );
}

/* ── Tab: OSRM ──────────────────────────────────────────────────────── */
function OsrmTab() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  return (
    <>
      <TabHeader title="Tracés des lignes (OSRM)" icon={MapIcon}
        subtitle="Génère automatiquement le tracé routier exact de chaque tournée via OSRM (OpenStreetMap routing, gratuit, sans clé)." />
      <p className="text-[11px] font-mono text-zinc-500 mb-4">
        ⏱️ ~1 seconde par tournée (limite OSRM publique). Pour 16 tournées : ~20 s.
      </p>
      <button data-testid="osrm-regenerate-btn" disabled={running}
        onClick={async () => {
          if (!confirm("Régénérer tous les tracés via OSRM ? (remplace les tracés actuels)")) return;
          setRunning(true); setResult(null);
          try {
            const { data } = await api.post("/logibus/admin/regenerate-shapes-osrm", null, { timeout: 600000 });
            setResult(data);
            toast.success(`${data.ok}/${data.processed} tournées · ${data.shape_points} points`);
          } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
          finally { setRunning(false); }
        }}
        className="bg-zinc-900 text-[#FFC60A] border border-zinc-900 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1">
        {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Régénérer via OSRM
      </button>
      {result && (
        <div className="mt-3 border border-emerald-700 bg-emerald-50 p-3 text-xs font-mono" data-testid="osrm-result">
          ✅ {result.ok}/{result.processed} tournées · {result.shape_points} points générés
          {result.failed > 0 && <span className="text-orange-700"> · {result.failed} échec(s)</span>}
        </div>
      )}
    </>
  );
}

/* ── Tab: GTFS import ───────────────────────────────────────────────── */
function GtfsTab() {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  return (
    <>
      <TabHeader title="Import GTFS officiel Suisse" icon={Database}
        subtitle="Importez le fichier GTFS officiel pour obtenir les vrais tracés des lignes qui suivent les routes." />
      <p className="text-xs text-zinc-600 mb-3">
        Fichier source : <a className="underline" target="_blank" rel="noreferrer" href="https://opentransportdata.swiss/dataset/timetable-2025-gtfs2020">OpenTransportData.swiss <ExternalLink size={11} className="inline" /></a>
      </p>
      <ol className="list-decimal list-inside text-[11px] font-mono space-y-1 mb-4 text-zinc-600">
        <li>Téléchargez le fichier <code className="bg-zinc-100 px-1">gtfs_fp2025_*.zip</code> (~150 MB)</li>
        <li>Importez ci-dessous (filtrage auto Morges/Rolle, ~30–60 s)</li>
        <li>La carte « Suivi live » utilisera ensuite les vrais tracés</li>
      </ol>
      <div className="flex flex-wrap items-center gap-2">
        <input type="file" accept=".zip" data-testid="gtfs-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-xs font-mono file:mr-2 file:border file:border-zinc-900 file:bg-zinc-50 file:px-2 file:py-1 file:font-bold file:uppercase file:tracking-widest" />
        <button data-testid="upload-gtfs-btn" disabled={!file || importing}
          onClick={async () => {
            if (!file) return;
            setImporting(true); setResult(null);
            try {
              const fd = new FormData(); fd.append("file", file);
              const { data } = await api.post("/logibus/admin/import-gtfs-upload", fd, {
                headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
              });
              setResult(data);
              toast.success(`GTFS importé : ${data.stops} arrêts · ${data.routes} lignes`);
            } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
            finally { setImporting(false); }
          }}
          className="bg-zinc-900 text-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1">
          {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Importer GTFS
        </button>
        {file && !importing && (
          <span className="text-[10px] font-mono text-zinc-500">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</span>
        )}
      </div>
      {result && (
        <div className="mt-3 border border-emerald-700 bg-emerald-50 p-3 text-xs font-mono" data-testid="gtfs-result">
          ✅ <strong>{result.zip_filename}</strong> ({result.zip_size_mb} MB)<br />
          <span className="text-zinc-700">
            {result.stops} arrêts · {result.routes} lignes · {result.trips} trips · {result.shapes} points
          </span>
        </div>
      )}
    </>
  );
}

/* ── Tab: Mirror trips ──────────────────────────────────────────────── */
function MirrorTab() {
  return (
    <>
      <TabHeader title="Tournées miroir (après-midi / soir)" icon={Sparkles}
        subtitle="Génère automatiquement des tournées miroir à partir des tournées du matin (idempotent — pas de doublon)." />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {[
          { slot: "midday",    label: "Midi",       offset: "+5h" },
          { slot: "afternoon", label: "Après-midi", offset: "+8h" },
          { slot: "evening",   label: "Soir",       offset: "+10h" },
        ].map(({ slot, label, offset }) => (
          <GenSlotButton key={slot} slot={slot} label={label} offset={offset} />
        ))}
      </div>
    </>
  );
}

function GenSlotButton({ slot, label, offset }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [reverse, setReverse] = useState(true);
  return (
    <div className="border border-zinc-900 p-3 bg-white">
      <div className="font-display font-bold text-sm tracking-tight">{label}</div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Décalage {offset}</div>
      <label className="flex items-center gap-2 text-xs mb-2">
        <input type="checkbox" checked={reverse} onChange={(e) => setReverse(e.target.checked)} />
        Inverser l&apos;itinéraire (retour école)
      </label>
      <button
        onClick={async () => {
          setRunning(true);
          try {
            const { data } = await api.post("/admin/gtfs/generate-from-morning", { slot, reverse_direction: reverse });
            setResult(data);
            if (data.created > 0) toast.success(`${data.created} tournée(s) ${label.toLowerCase()} créée(s)`);
            else toast.info(`Aucune nouvelle tournée (${data.skipped_existing} déjà présentes)`);
          } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
          finally { setRunning(false); }
        }}
        disabled={running}
        data-testid={`generate-${slot}`}
        className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 bg-zinc-900 text-[#FFC60A] text-[11px] font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-60">
        {running ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Générer
      </button>
      {result && (
        <div className="mt-2 text-[10px] font-mono text-zinc-600">
          ✓ {result.created} créées · {result.skipped_existing} ignorées
        </div>
      )}
    </div>
  );
}

/* ── Tab: Reset (danger zone) ───────────────────────────────────────── */
function ResetSchoolDataSection() {
  const [step, setStep] = useState(0);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const reset = () => { setStep(0); setTyped(""); setResult(null); };
  const launch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/school-data/reset", { confirm: "RESET", confirm_text: "RÉINITIALISER" });
      setResult(data);
      toast.success("Données scolaires réinitialisées");
      setStep(0); setTyped("");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <section className="border-2 border-red-600 bg-red-50/60 p-5" data-testid="reset-school-data-section">
      <div className="flex items-start gap-3">
        <AlertTriangle size={22} className="text-red-700 shrink-0 mt-1" />
        <div className="flex-1">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-red-700 font-bold">Zone dangereuse</div>
          <h2 className="font-display font-black text-2xl tracking-tight text-red-900 mt-1">Réinitialiser les données scolaires</h2>
          <p className="text-sm text-zinc-700 mt-2 max-w-2xl">
            Supprime de manière définitive toutes les données générées par les imports Excel scolaires :
            tournées scolaires, arrêts d&apos;import, itinéraires générés, planned services, affectations
            liées aux tournées importées et tous les templates d&apos;import.
          </p>
          <p className="text-xs text-zinc-600 mt-2 max-w-2xl">
            <strong>Ne sont PAS supprimés</strong> : utilisateurs, véhicules, chauffeurs, tags Bluetooth,
            paramètres système, données GPS/Logitrak.
          </p>

          {step === 0 && (
            <button onClick={() => setStep(1)} data-testid="reset-school-data-open"
              className="mt-4 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest shadow-[3px_3px_0_0_#0A0A0B] border border-zinc-900">
              <Trash2 size={14} /> Réinitialiser les données scolaires
            </button>
          )}
          {step === 1 && (
            <div className="mt-4 border border-red-700 bg-white p-4">
              <div className="font-bold text-red-800 text-sm">Confirmation 1/2</div>
              <p className="text-sm text-zinc-700 mt-1">Cette action est <strong>irréversible</strong>. Voulez-vous vraiment continuer ?</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setStep(2)} data-testid="reset-school-data-step2" className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 text-xs font-bold uppercase">Oui, continuer</button>
                <button onClick={reset} data-testid="reset-school-data-cancel-1" className="border border-zinc-900 px-3 py-1.5 text-xs font-bold uppercase hover:bg-zinc-100">Annuler</button>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="mt-4 border border-red-700 bg-white p-4">
              <div className="font-bold text-red-800 text-sm">Confirmation 2/2</div>
              <p className="text-sm text-zinc-700 mt-1">
                Pour valider, tapez exactement <code className="font-mono bg-zinc-900 text-[#FFC60A] px-1.5 py-0.5">RÉINITIALISER</code> ci-dessous :
              </p>
              <input type="text" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="RÉINITIALISER" data-testid="reset-school-data-input" autoFocus
                className="mt-2 w-full border-2 border-red-600 px-3 py-2 font-mono text-sm uppercase tracking-widest" />
              <div className="flex gap-2 mt-3">
                <button onClick={launch} disabled={typed.trim().toUpperCase() !== "RÉINITIALISER" || busy} data-testid="reset-school-data-confirm"
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 text-xs font-bold uppercase inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Supprimer définitivement
                </button>
                <button onClick={reset} data-testid="reset-school-data-cancel-2" className="border border-zinc-900 px-3 py-1.5 text-xs font-bold uppercase hover:bg-zinc-100">Annuler</button>
              </div>
            </div>
          )}
          {result && (
            <div className="mt-4 border border-emerald-700 bg-emerald-50 p-3 text-xs font-mono" data-testid="reset-school-data-result">
              <div className="font-bold text-emerald-900 mb-1">✓ Réinitialisation terminée</div>
              {Object.entries(result.deleted || {}).map(([k, v]) => (
                <div key={k} className="text-zinc-700">{k} : <strong>{v}</strong> supprimés</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
